import type { Period } from '@/entities/types';
import { useReportQueryError } from '@/hooks/useReportQueryError';
import { logError } from '@/services/logger';
import { getPeriodStartAt } from '@/utils/datetime';
import {
  formatMonthDayYear,
  formatQuarterRange,
  formatRelativeDay,
  formatRelativeQuarter,
  formatRelativeWeek,
  formatRelativeYear,
  formatWeekdayMonthDayYear,
  formatWeekRange,
  formatYearRange,
} from '@/utils/format-datetime';
import { useOrCreatePlan } from '@/queries/plan';
import { GoalsList } from '../GoalsList';
import { PeriodPaneDisplay } from '../PeriodPaneDisplay';

function getDaySubtitle(dayStartAt: Date, dayTitle: string): string {
  const isSpecialDay = ['Today', 'Yesterday', 'Tomorrow'].includes(dayTitle);
  return isSpecialDay
    ? formatWeekdayMonthDayYear(dayStartAt)
    : formatMonthDayYear(dayStartAt);
}

function getPeriodDisplay(
  period: Period,
  periodStartAt: Date,
): { title: string; subtitle: string } {
  const now = new Date();
  if (period === 'day') {
    const title = formatRelativeDay(now, periodStartAt);
    return { title, subtitle: getDaySubtitle(periodStartAt, title) };
  }
  if (period === 'week') {
    return {
      title: formatRelativeWeek(now, periodStartAt),
      subtitle: formatWeekRange(periodStartAt),
    };
  }
  if (period === 'quarter') {
    return {
      title: formatRelativeQuarter(now, periodStartAt),
      subtitle: formatQuarterRange(periodStartAt),
    };
  }
  return {
    title: formatRelativeYear(now, periodStartAt),
    subtitle: formatYearRange(periodStartAt),
  };
}

interface MainPlanProps {
  period: Period;
  targetStartAt: Date;
  targetStartAtLocal: string;
  className?: string;
}

export function MainPlan({
  className,
  period,
  targetStartAt,
  targetStartAtLocal,
}: MainPlanProps) {
  const { plan, error: planError } = useOrCreatePlan(
    period,
    targetStartAt,
    targetStartAtLocal,
  );

  useReportQueryError(planError, 'MainPlan');

  let periodStartAt: Date;
  try {
    periodStartAt = getPeriodStartAt(targetStartAt, period);
  } catch (error) {
    logError(error, 'MainPlan getPeriodStartAt failed', {
      period,
      targetStartAtIso: targetStartAt.toISOString(),
      targetStartAtString: targetStartAt.toString(),
      targetStartAtLocal,
      timezoneOffset: targetStartAt.getTimezoneOffset(),
    });
    throw error;
  }
  const { title, subtitle } = getPeriodDisplay(period, periodStartAt);

  if (planError) {
    return <div>Error loading plan</div>;
  }

  return (
    <PeriodPaneDisplay
      title={title}
      subtitle={subtitle}
      period={period}
      periodStartAt={periodStartAt}
      className={className}
    >
      {plan && (
        <GoalsList
          key={plan.id}
          planId={plan.id}
          period={period}
          startAtLocal={targetStartAtLocal}
          focusOnMount
          showPlaceholder
        />
      )}
    </PeriodPaneDisplay>
  );
}
